import { useState, type ChangeEvent, type FormEvent } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { describeSiteError } from "../../api/http"
import { keys } from "../../api/keys"
import { catalogAdminWrites } from "../../api/mutations"
import type { NewSiteFields } from "../../api/types"
import { ActionNotes } from "../../components/Note"
import { useAction } from "../../hooks/useAction"

type Kind = "site" | "printed"

const EMPTY_SITE: NewSiteFields = {
	siteName: "",
	serverIp: "",
	port: "3306",
	dbName: "",
	dbUser: "",
	dbPassword: "",
	ftpHost: "",
	ftpPath: "",
	ftpUser: "",
	ftpPassword: "",
}

/**
 * Создание нового сайта или печатного каталога — прямо с `/publish`, без
 * похода в базу руками.
 *
 * Сайт заводится сразу со схемой «Латвия» (`Schema = 2` — Рига не ведёт
 * других) и парным каталогом того же имени в одной транзакции
 * (`CatalogDirectory.CreateSiteAsync`): без него публикация не находит
 * каталог сайта, поэтому отдельно каталог заводить не просят. Печатный
 * каталог — только имя, остальные поля ему не нужны.
 *
 * Оба сразу попадают в списки Риги на этой странице (`useRigaCatalogs`,
 * `useLatvianWebsites`) — самообслуживание, без правки конфига и передеплоя:
 * сайт виден по схеме, печатный каталог — по реестру узла
 * (`CatalogRegistry`, `/ai/catalog/printed-ids`).
 *
 * `Admin`-only на бэке: форма кладёт в ERP боевые реквизиты FTP и БД сайта.
 */
export default function CreateSiteOrCatalog() {
	const client = useQueryClient()
	const [kind, setKind] = useState<Kind>("site")
	const [printedName, setPrintedName] = useState("")
	const [site, setSite] = useState<NewSiteFields>(EMPTY_SITE)

	const action = useAction({
		describe: describeSiteError,
		after: () =>
			Promise.all([
				client.invalidateQueries({ queryKey: keys.catalogs() }),
				client.invalidateQueries({ queryKey: keys.websites() }),
				client.invalidateQueries({ queryKey: keys.printedCatalogIds() }),
			]),
	})

	function setField(key: keyof NewSiteFields) {
		return (event: ChangeEvent<HTMLInputElement>) =>
			setSite(prev => ({ ...prev, [key]: event.target.value }))
	}

	async function submit(event: FormEvent) {
		event.preventDefault()

		if (kind === "printed") {
			const name = printedName.trim()
			if (!name) return
			void action.run(async () => {
				const created = await catalogAdminWrites.createPrinted(name)
				setPrintedName("")
				action.setNotice(`Печатный каталог «${created.name}» создан, id ${created.id}.`)
			})
			return
		}

		void action.run(async () => {
			const created = await catalogAdminWrites.createSite(site)
			setSite(EMPTY_SITE)
			action.setNotice(
				`Сайт «${created.name}» создан: id сайта ${created.websiteId}, id каталога ${created.catalogId}. ` +
					"Уже виден в списках сайтов и каталогов Риги на этой странице."
			)
		})
	}

	const printedValid = printedName.trim().length > 0
	const siteValid = Object.values(site).every(value => value.trim().length > 0)
	const valid = kind === "printed" ? printedValid : siteValid

	return (
		<details className="panel pub-fold">
			<summary className="panel__head">
				<span>Создать сайт или каталог</span>
				<span className="panel__spacer" />
				<span className="muted">только для администратора</span>
			</summary>

			<div className="panel__body">
				<div className="tabs">
					<button
						type="button"
						aria-selected={kind === "site"}
						onClick={() => setKind("site")}
					>
						Сайт
					</button>
					<button
						type="button"
						aria-selected={kind === "printed"}
						onClick={() => setKind("printed")}
					>
						Печатный каталог
					</button>
				</div>

				<form onSubmit={submit}>
					{kind === "printed" ? (
						<div className="field">
							<label className="field__label" htmlFor="new-catalog-name">
								Название каталога
							</label>
							<input
								id="new-catalog-name"
								className="input"
								value={printedName}
								onChange={event => setPrintedName(event.target.value)}
								placeholder="Например, «Каталог Рига 2027»"
							/>
						</div>
					) : (
						<>
							<p className="muted pub-lead">
								Заведёт сайт со схемой «Латвия» и одноимённый каталог — так его
								находит публикация. Все поля обязательны: без ФТП и подключения к
								БД сайт нельзя ни собрать, ни выгрузить на него.
							</p>

							<div className="field">
								<label className="field__label" htmlFor="site-name">
									Имя сайта
								</label>
								<input
									id="site-name"
									className="input"
									value={site.siteName}
									onChange={setField("siteName")}
									placeholder="www.avtodelo.xx"
								/>
							</div>

							<div className="fields-grid">
								<Field
									label="Сервер БД"
									value={site.serverIp}
									onChange={setField("serverIp")}
								/>
								<Field label="Порт" value={site.port} onChange={setField("port")} />
								<Field
									label="Имя БД"
									value={site.dbName}
									onChange={setField("dbName")}
								/>
								<Field
									label="Пользователь БД"
									value={site.dbUser}
									onChange={setField("dbUser")}
								/>
								<Field
									label="Пароль БД"
									type="password"
									value={site.dbPassword}
									onChange={setField("dbPassword")}
								/>
								<Field
									label="FTP-хост"
									value={site.ftpHost}
									onChange={setField("ftpHost")}
								/>
								<Field
									label="FTP-путь"
									value={site.ftpPath}
									onChange={setField("ftpPath")}
								/>
								<Field
									label="FTP-пользователь"
									value={site.ftpUser}
									onChange={setField("ftpUser")}
								/>
								<Field
									label="FTP-пароль"
									type="password"
									value={site.ftpPassword}
									onChange={setField("ftpPassword")}
								/>
							</div>
						</>
					)}

					<div className="pub-actions">
						<button className="btn btn--primary" disabled={action.busy || !valid}>
							{kind === "printed" ? "Создать каталог" : "Создать сайт"}
						</button>
						{action.busy && <span className="spinner" />}
					</div>
				</form>

				<ActionNotes action={action} />
			</div>
		</details>
	)
}

function Field({
	label,
	value,
	onChange,
	type = "text",
}: {
	label: string
	value: string
	onChange: (event: ChangeEvent<HTMLInputElement>) => void
	type?: "text" | "password"
}) {
	return (
		<div className="field">
			<label className="field__label">{label}</label>
			<input
				className="input"
				type={type}
				autoComplete="off"
				value={value}
				onChange={onChange}
			/>
		</div>
	)
}
